import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  ClipboardDocumentCheckIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase-auth';
import { formatDistanceToNow } from 'date-fns';

interface ComplianceDocument {
  id: string;
  document_type: 'needs_analysis' | 'reason_why';
  document_number: string;
  status: 'draft' | 'pending_signature' | 'signed' | 'archived';
  created_at: string;
  updated_at: string;
  signed_at: string | null;
  retention_until: string;
  current_version?: {
    id: string;
    version_number: number;
    created_at: string;
    created_by?: {
      full_name: string;
    };
  };
  document_signatures?: Array<{
    signer_type: 'client' | 'advisor';
    signed_at: string | null;
  }>;
}

interface ClientComplianceDocumentsProps {
  clientId: string;
  clientName: string;
}

export default function ClientComplianceDocuments({ clientId, clientName }: ClientComplianceDocumentsProps) {
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'needs_analysis' | 'reason_why'>('all');
  const [groupBy, setGroupBy] = useState<'date' | 'type' | 'status'>('date'); // Default to date grouping

  useEffect(() => {
    if (clientId) {
      fetchDocuments();
    }
  }, [clientId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('compliance_documents')
        .select(`*,current_version:document_versions!compliance_documents_current_version_id_fkey(id,version_number,created_at,created_by:platform_users(full_name)),document_signatures(signer_type,signed_at)`)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching compliance documents:', error);
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { icon: PencilSquareIcon, text: 'Draft', class: 'bg-gray-100 text-gray-700' },
      pending_signature: { icon: ClockIcon, text: 'Pending', class: 'bg-yellow-100 text-yellow-700' },
      signed: { icon: CheckCircleIcon, text: 'Signed', class: 'bg-green-100 text-green-700' },
      archived: { icon: DocumentTextIcon, text: 'Archived', class: 'bg-gray-100 text-gray-500' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const getDocumentIcon = (type: string) => {
    return type === 'needs_analysis' ? ClipboardDocumentCheckIcon : DocumentTextIcon;
  };

  const getDocumentTypeDisplay = (type: string) => {
    return type === 'needs_analysis' ? 'Needs Analysis' : 'Reason Why Letter';
  };

  const filteredDocuments = documents.filter(doc => {
    if (activeTab === 'all') return true;
    return doc.document_type === activeTab;
  });

  const groupDocuments = () => {
    if (groupBy === 'date') {
      const groups: Record<string, ComplianceDocument[]> = {};
      filteredDocuments.forEach(doc => {
        const date = new Date(doc.created_at);
        // Group by year and month for better organization
        const year = date.getFullYear();
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const groupKey = `${year} - ${month}`;
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(doc);
      });
      // Sort groups by date (newest first)
      const sortedGroups: Record<string, ComplianceDocument[]> = {};
      Object.keys(groups)
        .sort((a, b) => {
          const dateA = new Date(groups[a][0].created_at);
          const dateB = new Date(groups[b][0].created_at);
          return dateB.getTime() - dateA.getTime();
        })
        .forEach(key => {
          sortedGroups[key] = groups[key].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        });
      return sortedGroups;
    } else if (groupBy === 'type') {
      const groups: Record<string, ComplianceDocument[]> = {};
      filteredDocuments.forEach(doc => {
        const type = getDocumentTypeDisplay(doc.document_type);
        if (!groups[type]) groups[type] = [];
        groups[type].push(doc);
      });
      return groups;
    } else {
      const groups: Record<string, ComplianceDocument[]> = {};
      filteredDocuments.forEach(doc => {
        if (!groups[doc.status]) groups[doc.status] = [];
        groups[doc.status].push(doc);
      });
      return groups;
    }
  };

  const documentGroups = groupDocuments();

  // Calculate stats
  const stats = {
    total: documents.length,
    signed: documents.filter(d => d.status === 'signed').length,
    pending: documents.filter(d => d.status === 'pending_signature').length,
    drafts: documents.filter(d => d.status === 'draft').length
  };

  const lastNeedsAnalysis = documents
    .filter(d => d.document_type === 'needs_analysis' && d.status === 'signed')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  const handleAction = (doc: ComplianceDocument, action: 'view' | 'edit' | 'download') => {
    switch (action) {
      case 'edit':
        window.location.href = `/portal/compliance/edit/${doc.id}`;
        break;
      case 'view':
        window.location.href = `/portal/compliance/view/${doc.id}`;
        break;
      case 'download':
        // TODO: Implement PDF download
        // console.log('Download document:', doc.id);
        break;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compact Stats Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mb-2">
              <DocumentTextIcon className="h-6 w-6 text-gray-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-2">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.signed}</p>
            <p className="text-sm text-gray-500">Signed</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mb-2">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-gray-500">Pending</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mb-2">
              <PencilSquareIcon className="h-6 w-6 text-gray-600" />
            </div>
            <p className="text-2xl font-bold text-gray-600">{stats.drafts}</p>
            <p className="text-sm text-gray-500">Drafts</p>
          </div>
        </div>
      </div>

      {/* Quick Actions & Alerts */}
      {lastNeedsAnalysis ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-blue-900">Last Needs Analysis</h4>
              <p className="text-sm text-blue-700">
                Completed {formatDistanceToNow(new Date(lastNeedsAnalysis.created_at), { addSuffix: true })}
              </p>
              {/* Show warning if older than 6 months */}
              {new Date(lastNeedsAnalysis.created_at) < new Date(Date.now() - 180 * 24 * 60 * 60 * 1000) && (
                <p className="text-xs text-amber-700 mt-1">
                  <ExclamationTriangleIcon className="h-3 w-3 inline mr-1" />
                  Consider updating - over 6 months old
                </p>
              )}
            </div>
            <Link
              to={`/portal/compliance/wizard/needs_analysis?clientId=${clientId}`}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Quick Update
              <ChevronRightIcon className="ml-1 h-3 w-3" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-900">No Needs Analysis on File</h4>
                <p className="text-sm text-red-700">Required for compliance - Start one now</p>
              </div>
            </div>
            <Link
              to={`/portal/compliance/wizard/needs_analysis?clientId=${clientId}`}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              Start Needs Analysis
              <ChevronRightIcon className="ml-1 h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* Main Documents Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Compliance Documents</h3>
              <p className="text-sm text-gray-500 mt-1">Track and manage all compliance documentation</p>
            </div>
            <Link
              to={`/portal/compliance/new?clientId=${clientId}`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 transition-all shadow-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Document
            </Link>
          </div>
        </div>

        {/* Tabs and Filters */}
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex space-x-6">
              <button
                onClick={() => setActiveTab('all')}
                className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                  activeTab === 'all'
                    ? 'text-amber-600 border-amber-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                All Documents ({documents.length})
              </button>
              <button
                onClick={() => setActiveTab('needs_analysis')}
                className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                  activeTab === 'needs_analysis'
                    ? 'text-amber-600 border-amber-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Needs Analysis ({documents.filter(d => d.document_type === 'needs_analysis').length})
              </button>
              <button
                onClick={() => setActiveTab('reason_why')}
                className={`text-sm font-medium pb-2 border-b-2 transition-colors ${
                  activeTab === 'reason_why'
                    ? 'text-amber-600 border-amber-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Reason Why ({documents.filter(d => d.document_type === 'reason_why').length})
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Group by:</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as any)}
                className="text-sm border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="date">Date (Month)</option>
                <option value="type">Document Type</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>
        </div>

        {/* Document List */}
        <div className="divide-y divide-gray-200">
          {Object.entries(documentGroups).map(([groupName, groupDocs], idx) => {
            // Check if this is a new year when grouping by date
            const currentYear = groupName.split(' - ')[0];
            const prevGroupName = Object.keys(documentGroups)[idx - 1];
            const prevYear = prevGroupName ? prevGroupName.split(' - ')[0] : null;
            const isNewYear = groupBy === 'date' && currentYear !== prevYear && idx > 0;
            
            return (
            <React.Fragment key={groupName}>
              {isNewYear && (
                <div className="px-6 py-3 bg-gray-800 text-white text-sm font-semibold">
                  {currentYear}
                </div>
              )}
            <div>
              <div className="px-6 py-2 bg-gray-50 flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">{groupName}</h4>
                <span className="text-xs text-gray-500">{groupDocs.length} document{groupDocs.length !== 1 ? 's' : ''}</span>
              </div>
              {groupDocs.map((doc) => {
                const Icon = getDocumentIcon(doc.document_type);
                return (
                  <div key={doc.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Icon className="h-6 w-6 text-gray-600" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900">
                              {getDocumentTypeDisplay(doc.document_type)}
                            </h4>
                            {getStatusBadge(doc.status)}
                            {doc.current_version && (
                              <span className="text-xs text-gray-500">
                                v{doc.current_version.version_number}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {new Date(doc.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            {doc.signed_at && (
                              <span className="flex items-center text-green-600">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Signed {new Date(doc.signed_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            )}
                            <span className="text-xs text-gray-400">
                              {doc.document_number}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {doc.status === 'draft' ? (
                          <button
                            onClick={() => handleAction(doc, 'edit')}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                          >
                            <PencilSquareIcon className="h-4 w-4 mr-1" />
                            Continue
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleAction(doc, 'view')}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              View
                            </button>
                            {doc.status === 'signed' && (
                              <button
                                onClick={() => handleAction(doc, 'download')}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                PDF
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </React.Fragment>
          );
          })}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="px-6 py-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new compliance document.
            </p>
            <div className="mt-6">
              <Link
                to={`/portal/compliance/new?clientId=${clientId}`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Document
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}